import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  SlidersHorizontal,
  CheckCircle2,
  Pencil,
  Trash2,
  Plus,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../lib/supabase';
import {
  VALIDATION_PROFILE_OPTIONS,
  defaultValidationProfile,
} from '../lib/validationProfileOptions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * The eight validation categories the backend pipeline understands. Mirrors
 * ``_ALLOWED_KEYS`` in backend/routes/validation_profiles.py — the server
 * rejects any other key with HTTP 422, so keep these in sync.
 *
 * The list itself lives in ../lib/validationProfileOptions so it stays
 * synchronised with the "run new audit" step on ProjectDetailPage.
 */
const PROFILE_KEYS = VALIDATION_PROFILE_OPTIONS.map((o) => ({
  key: o.key,
  label: o.label,
  description: o.description,
}));

/**
 * Local presets shown when the server has zero saved profiles. The user can
 * "Save preset as my own" to copy one into their own list — at which point
 * it becomes a real persisted row they can edit / delete. We never write
 * the presets themselves to the server (they're documentation).
 */
const PRESETS = [
  {
    id: 'preset-full',
    name: 'Full handover audit',
    description: 'Strictest profile — use before practical-completion handover.',
    profile: Object.fromEntries(PROFILE_KEYS.map((k) => [k.key, true])),
  },
  {
    id: 'preset-schema',
    name: 'Schema & naming only',
    description: 'Quick pre-flight — naming + IFC schema. Skips FM/COBie/O&M.',
    profile: {
      ...defaultValidationProfile(false),
      naming_compliance: true,
      ifc_schema: true,
    },
  },
  {
    id: 'preset-fm',
    name: 'FM readiness',
    description: 'Focus on operations — FM parameters, COBie, O&M matching.',
    profile: {
      ...defaultValidationProfile(false),
      fm_parameters: true,
      cobie_handover: true,
      om_matching: true,
    },
  },
];

const _emptyProfile = () => defaultValidationProfile(false);

export default function ValidationProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorState, setEditorState] = useState(null); // null | {mode, profile}

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await apiFetch(`${API_URL}/api/validation-profiles`);
      if (!r.ok) throw new Error(`Could not load profiles (HTTP ${r.status})`);
      const j = await r.json();
      setProfiles(j.profiles || []);
    } catch (e) {
      setError(e.message || 'Could not load profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = (seed = null) => {
    setEditorState({
      mode: 'create',
      draft: {
        id: null,
        name: seed?.name ? `${seed.name} (copy)` : '',
        description: seed?.description || '',
        profile: seed?.profile ? { ..._emptyProfile(), ...seed.profile } : _emptyProfile(),
      },
    });
  };

  const openEdit = (p) => {
    setEditorState({
      mode: 'edit',
      draft: {
        id: p.id,
        name: p.name,
        description: p.description,
        profile: { ..._emptyProfile(), ...(p.profile || {}) },
      },
    });
  };

  const handleSave = async (draft) => {
    const isUpdate = !!draft.id;
    const url = isUpdate
      ? `${API_URL}/api/validation-profiles/${draft.id}`
      : `${API_URL}/api/validation-profiles`;
    try {
      const r = await apiFetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: draft.name,
          description: draft.description,
          profile: draft.profile,
        }),
      });
      if (!r.ok) {
        const body = await r.text();
        throw new Error(`Save failed (HTTP ${r.status}): ${body.slice(0, 200)}`);
      }
      toast.success(isUpdate ? 'Profile updated' : 'Profile saved', {
        description: draft.name,
      });
      setEditorState(null);
      await reload();
    } catch (e) {
      toast.error('Save failed', { description: e.message });
    }
  };

  const handleDelete = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete profile "${p.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      const r = await apiFetch(`${API_URL}/api/validation-profiles/${p.id}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error(`Delete failed (HTTP ${r.status})`);
      toast.success('Profile deleted', { description: p.name });
      await reload();
    } catch (e) {
      toast.error('Delete failed', { description: e.message });
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="validation-profiles-page">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
              <SlidersHorizontal className="h-7 w-7 text-primary" /> Validation Profiles
            </h1>
            <p className="text-muted-foreground">
              Save named combinations of validation categories and apply them when starting
              a new audit. Skips checks that don't apply at the current project stage.
            </p>
          </div>
          <Button
            onClick={() => openCreate()}
            data-testid="profiles-new-button"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1" /> New profile
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="profiles-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* — Saved profiles list — */}
        <h2
          className="text-lg font-semibold text-foreground mb-3"
          data-testid="profiles-saved-heading"
        >
          Saved profiles
        </h2>
        <Card className="mb-8">
          <CardContent className="p-0">
            {loading ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                Loading profiles…
              </div>
            ) : profiles.length === 0 ? (
              <div
                className="px-6 py-10 text-center text-sm text-muted-foreground"
                data-testid="profiles-empty"
              >
                <p className="font-medium text-foreground mb-1">
                  No saved profiles yet.
                </p>
                <p>
                  Start one of the suggested presets below as a template, or click
                  "New profile" above.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {profiles.map((p) => (
                  <ProfileRow
                    key={p.id}
                    profile={p}
                    onEdit={() => openEdit(p)}
                    onDelete={() => handleDelete(p)}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* — Preset cards (templates) — */}
        <h2
          className="text-lg font-semibold text-foreground mb-3"
          data-testid="profiles-presets-heading"
        >
          Suggested presets
        </h2>
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          {PRESETS.map((preset) => (
            <Card
              key={preset.id}
              data-testid={`profile-preset-${preset.id}`}
              className="flex flex-col"
            >
              <CardHeader>
                <CardTitle className="text-base">{preset.name}</CardTitle>
                <CardDescription>{preset.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-1.5">
                  {PROFILE_KEYS.map((k) => {
                    const on = preset.profile[k.key];
                    return (
                      <li key={k.key} className="flex items-center gap-2 text-xs">
                        {on ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                          <span className="h-3.5 w-3.5 inline-block rounded-full border border-border" />
                        )}
                        <span className={on ? 'text-foreground' : 'text-muted-foreground line-through'}>
                          {k.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
              <div className="px-6 pb-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => openCreate(preset)}
                  data-testid={`profile-preset-${preset.id}-use-button`}
                >
                  Save as my own
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* — Category reference — */}
        <h2 className="text-lg font-semibold text-foreground mb-3">
          Validation categories reference
        </h2>
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {PROFILE_KEYS.map((k) => (
                <li
                  key={k.key}
                  className="px-4 py-3 flex items-start justify-between gap-4"
                  data-testid={`profile-category-${k.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{k.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{k.description}</p>
                  </div>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {k.key}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {editorState && (
        <ProfileEditorDialog
          state={editorState}
          onClose={() => setEditorState(null)}
          onSave={handleSave}
        />
      )}
    </DashboardLayout>
  );
}

function ProfileRow({ profile, onEdit, onDelete }) {
  const activeCount = Object.values(profile.profile || {}).filter(Boolean).length;
  return (
    <li
      className="px-6 py-4 flex items-center justify-between gap-4"
      data-testid={`profile-row-${profile.id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {profile.name}
        </p>
        {profile.description && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {profile.description}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground/80 mt-1">
          {activeCount} of {PROFILE_KEYS.length} categories enabled
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          data-testid={`profile-row-${profile.id}-edit`}
        >
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          data-testid={`profile-row-${profile.id}-delete`}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  );
}

function ProfileEditorDialog({ state, onClose, onSave }) {
  const [draft, setDraft] = useState(state.draft);
  const [submitting, setSubmitting] = useState(false);

  const togglePresent = (key) =>
    setDraft({ ...draft, profile: { ...draft.profile, [key]: !draft.profile[key] } });

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    if (Object.values(draft.profile).filter(Boolean).length === 0) {
      toast.error('Pick at least one category before saving.');
      return;
    }
    setSubmitting(true);
    try {
      await onSave(draft);
    } finally {
      setSubmitting(false);
    }
  };

  const isEdit = state.mode === 'edit';
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="profile-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit profile' : 'New validation profile'}</DialogTitle>
          <DialogDescription>
            Pick the categories the QG pipeline should run when this profile is applied.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="profile-name">Name</Label>
            <Input
              id="profile-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. WZB handover · structural"
              maxLength={80}
              required
              data-testid="profile-editor-name"
            />
          </div>
          <div>
            <Label htmlFor="profile-description">Description (optional)</Label>
            <Textarea
              id="profile-description"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="When to use this profile…"
              rows={2}
              maxLength={400}
              data-testid="profile-editor-description"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Categories
            </Label>
            <ul className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
              {PROFILE_KEYS.map((k) => (
                <li key={k.key} className="flex items-start gap-3">
                  <Checkbox
                    id={`cat-${k.key}`}
                    checked={!!draft.profile[k.key]}
                    onCheckedChange={() => togglePresent(k.key)}
                    data-testid={`profile-editor-cat-${k.key}`}
                  />
                  <div className="flex-1 min-w-0">
                    <Label htmlFor={`cat-${k.key}`} className="text-sm cursor-pointer">
                      {k.label}
                    </Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {k.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="profile-editor-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !draft.name.trim()}
              data-testid="profile-editor-save"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save profile'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
